import { create } from 'zustand';
import { 
  ChartState, 
  Chart, 
  ChartData, 
  CreateChartRequest, 
  UpdateChartRequest 
} from '@/types/chart';

interface ChartStore extends ChartState {
  setCharts: (charts: Chart[]) => void;
  setCurrentChart: (chart: Chart | null) => void;
  setChartData: (chartId: string, data: ChartData) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  loadChart: (id: string) => Promise<void>;
  createChart: (data: CreateChartRequest) => Promise<Chart>;
  updateChart: (id: string, data: UpdateChartRequest) => Promise<Chart>;
  deleteChart: (id: string) => Promise<void>;
  duplicateChart: (id: string) => Promise<Chart>;
  
  loadChartData: (id: string) => Promise<void>;
  refreshChartData: (id: string) => Promise<void>;
  
  resetChart: () => void;
}

export const useChartStore = create<ChartStore>((set, get) => ({
  charts: [],
  currentChart: null,
  chartData: {},
  isLoading: false,
  error: null,

  setCharts: (charts) => set({ charts }),
  setCurrentChart: (chart) => set({ currentChart: chart }),
  setChartData: (chartId, data) => set(state => ({ 
    chartData: { ...state.chartData, [chartId]: data } 
  })),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  loadChart: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      // Mock chart data
      const mockChart: Chart = {
        id,
        dashboard_id: 'dashboard-1',
        name: 'Sales Trends',
        type: 'line',
        config: {
          title: 'Monthly Sales Trends',
          subtitle: 'Revenue over time',
          colors: ['#3B82F6', '#10B981', '#F59E0B'],
          axes: {
            x: {
              label: 'Month',
              type: 'date',
              showGrid: true,
              showTicks: true,
            },
            y: {
              label: 'Revenue ($)',
              type: 'number',
              showGrid: true,
              showTicks: true,
            },
          },
          legend: {
            show: true,
            position: 'top',
            align: 'center',
          },
          tooltip: {
            show: true,
            backgroundColor: '#1F2937',
            textColor: '#FFFFFF',
          },
          animation: {
            enabled: true,
            duration: 300,
            easing: 'ease-out',
          },
          responsive: true,
          theme: {
            backgroundColor: '#FFFFFF',
            textColor: '#1F2937',
            gridColor: '#E5E7EB',
            borderColor: '#D1D5DB',
          },
        },
        data_source_id: 'datasource-1',
        query: {
          fields: [
            { name: 'month', alias: 'Month' },
            { name: 'revenue', alias: 'Revenue', aggregation: 'sum' },
          ],
          filters: [],
          groupBy: ['month'],
          orderBy: [{ field: 'month', direction: 'asc' }],
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      set({ currentChart: mockChart, isLoading: false });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load chart', isLoading: false });
    }
  },

  createChart: async (data: CreateChartRequest) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      const newChart: Chart = {
        id: `chart-${Date.now()}`,
        dashboard_id: data.dashboard_id,
        name: data.name,
        type: data.type,
        config: {
          title: data.name,
          colors: ['#3B82F6', '#10B981', '#F59E0B'],
          axes: {
            x: { label: 'X Axis', type: 'category', showGrid: true, showTicks: true },
            y: { label: 'Y Axis', type: 'number', showGrid: true, showTicks: true },
          },
          legend: { show: true, position: 'top', align: 'center' },
          tooltip: { show: true },
          animation: { enabled: true, duration: 300, easing: 'ease-out' },
          responsive: true,
          theme: {
            backgroundColor: '#FFFFFF',
            textColor: '#1F2937',
            gridColor: '#E5E7EB',
            borderColor: '#D1D5DB',
          },
          ...data.config,
        },
        data_source_id: data.data_source_id,
        query: data.query,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      set(state => ({
        charts: [...state.charts, newChart],
        currentChart: newChart,
        isLoading: false,
      }));
      
      return newChart;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create chart', isLoading: false });
      throw error;
    }
  },

  updateChart: async (id: string, data: UpdateChartRequest) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      const updatedChart = get().charts.find(c => c.id === id);
      if (!updatedChart) {
        throw new Error('Chart not found');
      }
      
      const updated: Chart = {
        ...updatedChart,
        ...data,
        config: {
          ...updatedChart.config,
          ...data.config,
        },
        updated_at: new Date().toISOString(),
      };
      
      set(state => ({
        charts: state.charts.map(c => c.id === id ? updated : c),
        currentChart: state.currentChart?.id === id ? updated : state.currentChart,
        isLoading: false,
      }));
      
      return updated;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update chart', isLoading: false });
      throw error;
    }
  },

  deleteChart: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      set(state => ({
        charts: state.charts.filter(c => c.id !== id),
        currentChart: state.currentChart?.id === id ? null : state.currentChart,
        chartData: Object.fromEntries(
          Object.entries(state.chartData).filter(([chartId]) => chartId !== id)
        ),
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete chart', isLoading: false });
      throw error;
    }
  },

  duplicateChart: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      const original = get().charts.find(c => c.id === id);
      if (!original) {
        throw new Error('Chart not found');
      }
      
      const duplicate: Chart = {
        ...original,
        id: `chart-${Date.now()}`,
        name: `${original.name} (Copy)`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      set(state => ({
        charts: [...state.charts, duplicate],
        isLoading: false,
      }));
      
      return duplicate;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to duplicate chart', isLoading: false });
      throw error;
    }
  },

  loadChartData: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      // Mock chart data
      const mockData: ChartData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'Revenue',
            data: [12000, 19000, 15000, 25000, 22000, 30000],
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: '#3B82F6',
            borderWidth: 2,
          },
          {
            label: 'Profit',
            data: [8000, 12000, 9000, 15000, 13000, 18000],
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderColor: '#10B981',
            borderWidth: 2,
          },
        ],
      };
      
      set(state => ({
        chartData: { ...state.chartData, [id]: mockData },
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load chart data', isLoading: false });
    }
  },

  refreshChartData: async (id: string) => {
    await get().loadChartData(id);
  },

  resetChart: () => {
    set({ currentChart: null, error: null });
  },
}));